// Phase RV-3 — RolloverIntelligenceView.
//
// Three sections per §05 Table 15:
//   1. SELECTED LEGS — full leg detail (Pro), or fixture+selection (free)
//   2. EVALUATED · passed filter, not selected — count + reasons (Pro)
//   3. EVALUATED · did not qualify at filter — grouped by filter_stage
//
// Plus, for skip cycles, the N12 SKIPPED CYCLE NOTE.
// Plus, for 4x/5x tiers with adversarial_log, the Adversarial Review.
//
// Promotion-not-restriction: copy never says "ERROR", "FAILED" or
// "REJECTED". Use "Did not qualify" or "Standard working as intended".
//
// Visual: Football Products (FpKit) Kalshi-clean. Inline styles, FpKit
// tokens, hairline borders, mono uppercase micro-labels.

import type { ReactNode } from "react";
import type {
  FilterStage,
  TrackerCandidate,
  TrackerCycle,
} from "../../rollover/useRolloverTracker.ts";

const STAGE_LABEL: Record<FilterStage, string> = {
  stage_1_scan: "Stage 1 · Scan",
  stage_2_form_value: "Stage 2 · Form & Value",
  stage_3_odds: "Stage 3 · Odds Band",
  stage_5_adversarial: "Stage 5 · Adversarial",
};

// --- FpKit inline primitives -------------------------------------------

type FpVariant = "amber" | "ghost";

function FpBadge({ variant, children }: { variant: FpVariant; children: ReactNode }): JSX.Element {
  const spec =
    variant === "amber"
      ? { bg: "var(--fp-accent-soft)", color: "var(--fp-accent-text)", border: "1px solid rgba(212,175,55,0.25)" }
      : { bg: "transparent", color: "var(--text-tertiary)", border: "1px solid var(--border-faint)" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        padding: "2px 7px",
        borderRadius: 4,
        background: spec.bg,
        color: spec.color,
        border: spec.border,
      }}
    >
      {children}
    </span>
  );
}

function FpPill({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 8px",
        borderRadius: 4,
        border: "1px solid var(--border-faint)",
        background: "var(--bg-inset)",
        color: "var(--text-tertiary)",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        letterSpacing: "0.12em",
        lineHeight: 1,
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

function FpEyebrow({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "var(--fp-accent-text)",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span aria-hidden style={{ width: 16, height: 1, background: "currentColor", opacity: 0.7 }} />
      {children}
    </div>
  );
}

function FpRow({
  children,
  first,
  padY = 14,
}: {
  children: ReactNode;
  first?: boolean;
  padY?: number;
}): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: `${padY}px 0`,
        borderTop: first ? "none" : "1px solid var(--border-faint)",
      }}
    >
      {children}
    </div>
  );
}

function topStage(breakdown: Record<string, number>): { stage: string; count: number } | null {
  const entries = Object.entries(breakdown);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  const first = entries[0];
  if (!first) return null;
  return { stage: first[0], count: first[1] };
}

export type ConversionCtaState =
  | "anon"
  | "trial_eligible"
  | "trial_used"
  | "trial_active"
  | "pro";

export interface ConversionCtaCopy {
  label: string;
  href: string;
}

/**
 * Pure resolver for the conversion CTA copy + route. Exported for tests.
 *
 * RV-4b doctrine:
 *   anon            → "Activate Pro to see the full agent log →" /auth?intent=trial
 *   trial_eligible  → "Start your 7-day trial — no card →"        /auth?intent=trial
 *   trial_used      → "Activate Pro →"                            /pricing
 *   trial_active    → "Trial active — agent log unlocked"         /pricing  (informational)
 *   pro             → no CTA (Pro already unlocked)
 */
export function resolveConversionCta(
  state: ConversionCtaState,
): ConversionCtaCopy | null {
  switch (state) {
    case "anon":
      return {
        label: "Activate Pro to see the full agent log →",
        href: "/auth?intent=trial",
      };
    case "trial_eligible":
      return {
        label: "Start your 7-day trial — no card →",
        href: "/auth?intent=trial",
      };
    case "trial_used":
      return { label: "Activate Pro →", href: "/pricing" };
    case "trial_active":
      return {
        label: "Trial active — agent log unlocked",
        href: "/pricing",
      };
    case "pro":
    default:
      return null;
  }
}

export interface RolloverIntelligenceViewProps {
  cycle: TrackerCycle;
  isPro: boolean;
  /** Conversion CTA state — caller derives from useUserTier + useTrial. */
  ctaState?: ConversionCtaState;
  /** Optional CTA route override (default: from resolveConversionCta). */
  proCtaHref?: string;
}

function LockedHint({
  href,
  label,
}: {
  href: string;
  label: string;
}): JSX.Element {
  return (
    <a
      href={href}
      data-test="rollover-conversion-cta"
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "var(--fp-accent-text)",
        textDecoration: "none",
      }}
    >
      {label}
    </a>
  );
}

function LegCard({
  cand,
  isPro,
}: {
  cand: TrackerCandidate;
  isPro: boolean;
}): JSX.Element {
  return (
    <div style={{ padding: "10px 0", width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--text)",
          }}
        >
          {cand.match_name}
        </span>
        {cand.league ? <FpPill>{cand.league}</FpPill> : null}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--text-tertiary)",
          marginTop: 4,
        }}
      >
        {(cand.market ?? "—")} · {(cand.pick ?? "—")} · {cand.odds !== null ? `${cand.odds.toFixed(2)}×` : "—"}
      </div>
      {isPro ? (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--text-secondary)",
            marginTop: 6,
            lineHeight: 1.5,
          }}
        >
          Confidence: {cand.confidence_score !== null ? `${cand.confidence_score.toFixed(0)}/100` : "—"}
          {cand.value_edge_pct !== null ? ` · CLV edge ${cand.value_edge_pct.toFixed(1)}%` : ""}
          {cand.agent_reasoning ? (
            <div style={{ marginTop: 4, color: "var(--text-secondary)" }}>
              {cand.agent_reasoning}
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ marginTop: 6 }}>
          <FpBadge variant="ghost">🔒 Confidence + reasoning · Pro</FpBadge>
        </div>
      )}
    </div>
  );
}

export function RolloverIntelligenceView({
  cycle,
  isPro,
  ctaState,
  proCtaHref,
}: RolloverIntelligenceViewProps): JSX.Element {
  const resolvedState: ConversionCtaState =
    ctaState ?? (isPro ? "pro" : "anon");
  const ctaCopy = resolveConversionCta(resolvedState);
  const ctaHref = proCtaHref ?? ctaCopy?.href ?? "/pricing";
  const ctaLabel = ctaCopy?.label ?? "Activate Pro to see what the agents see →";
  const { candidates, slip, tier } = cycle;
  const isSkip = !slip || slip.status === "skipped" || slip.outcome === "skipped";
  const isHighTier = tier === "4x" || tier === "5x" || tier === "6x";
  const adversarial =
    slip?.adversarial_log && Array.isArray(slip.adversarial_log)
      ? (slip.adversarial_log as unknown[])
      : [];
  const hasAdversarial = isHighTier && adversarial.length > 0;

  const rejectionGroups: Record<string, TrackerCandidate[]> = {};
  for (const c of [...candidates.evaluated, ...candidates.assessed]) {
    if (c.filter_stage_failed) {
      const key = c.filter_stage_failed;
      const arr = rejectionGroups[key] ?? [];
      arr.push(c);
      rejectionGroups[key] = arr;
    }
  }
  const passedNotSelected = candidates.evaluated.filter(
    (c) => !c.filter_stage_failed,
  );

  return (
    <div style={{ padding: "0 0 4px" }}>
      {isSkip ? (
        <div
          style={{
            padding: 14,
            border: "1px solid var(--border)",
            borderRadius: 10,
            marginBottom: 14,
            background: "var(--bg-inset)",
          }}
          data-test="skipped-cycle-note"
        >
          <FpEyebrow>Skipped Cycle Note</FpEyebrow>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--text-secondary)",
              marginTop: 10,
              lineHeight: 1.55,
            }}
          >
            Agents found no qualifying combination this cycle. This is the
            standard working as intended.{" "}
            {(() => {
              const top = topStage(candidates.rejection_breakdown);
              const scanned = candidates.total_scanned;
              if (!top) return `${scanned} candidates evaluated.`;
              return `${scanned} candidates evaluated; most-common reason: ${
                STAGE_LABEL[top.stage as FilterStage] ?? top.stage
              } (${top.count} did not qualify).`;
            })()}
          </div>
        </div>
      ) : null}

      {/* SELECTED LEGS */}
      <div style={{ padding: "0 0 14px" }}>
        <FpEyebrow>Selected Legs · {candidates.selected.length}</FpEyebrow>
        {candidates.selected.length === 0 ? (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--text-tertiary)",
              marginTop: 8,
            }}
          >
            No legs selected this cycle.
          </div>
        ) : (
          <div style={{ marginTop: 4 }}>
            {candidates.selected.map((c, idx) => (
              <FpRow key={c.candidate_id} first={idx === 0} padY={2}>
                <LegCard cand={c} isPro={isPro} />
              </FpRow>
            ))}
          </div>
        )}
      </div>

      {/* EVALUATED — passed filter, not selected */}
      <div style={{ padding: "0 0 14px" }}>
        <FpEyebrow>Evaluated · passed filter, not selected</FpEyebrow>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--text-tertiary)",
            marginTop: 8,
          }}
        >
          {passedNotSelected.length} candidates passed the filter but were not
          selected for this slip.
        </div>
        {isPro ? (
          <div style={{ marginTop: 8 }}>
            {passedNotSelected.slice(0, 8).map((c, idx) => (
              <FpRow key={c.candidate_id} first={idx === 0} padY={8}>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      color: "var(--text-secondary)",
                    }}
                  >
                    {c.match_name} · {(c.market ?? "—")} · {(c.pick ?? "—")}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      color: "var(--text-tertiary)",
                      marginTop: 2,
                    }}
                  >
                    {c.discard_reason ?? "Did not match slip composition."}
                  </div>
                </div>
              </FpRow>
            ))}
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            <FpBadge variant="ghost">🔒 Per-leg reasons · Pro</FpBadge>
            <div style={{ marginTop: 8 }}>
              <LockedHint href={ctaHref} label={ctaLabel} />
            </div>
          </div>
        )}
      </div>

      {/* EVALUATED — did not qualify */}
      <div style={{ padding: "0 0 14px" }} data-test="rejection-section">
        <FpEyebrow>Evaluated · did not qualify at filter</FpEyebrow>
        {Object.keys(rejectionGroups).length === 0 ? (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--text-tertiary)",
              marginTop: 8,
            }}
          >
            All evaluated candidates passed the filter stages.
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            {Object.entries(rejectionGroups).map(([stage, list], idx) => (
              <FpRow key={stage} first={idx === 0} padY={10}>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <FpPill>{STAGE_LABEL[stage as FilterStage] ?? stage}</FpPill>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        color: "var(--text-tertiary)",
                      }}
                    >
                      {list.length} did not qualify
                    </span>
                  </div>
                  {isPro ? (
                    <div style={{ marginTop: 6 }}>
                      {list.slice(0, 6).map((c) => (
                        <div
                          key={c.candidate_id}
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 10,
                            color: "var(--text-tertiary)",
                            marginTop: 2,
                          }}
                        >
                          {c.match_name}: {c.discard_reason ?? "Did not qualify."}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ marginTop: 6 }}>
                      <FpBadge variant="ghost">🔒 Per-row reasoning · Pro</FpBadge>
                    </div>
                  )}
                </div>
              </FpRow>
            ))}
          </div>
        )}
        {!isPro && ctaCopy ? (
          <div style={{ marginTop: 10 }}>
            <LockedHint href={ctaHref} label={ctaLabel} />
          </div>
        ) : null}
      </div>

      {/* Adversarial Review — 4x/5x only when log present */}
      {hasAdversarial ? (
        <div style={{ padding: "0 0 14px" }} data-test="adversarial-section">
          <FpEyebrow>Adversarial Review · {tier.toUpperCase()}</FpEyebrow>
          {isPro ? (
            <div style={{ marginTop: 8 }}>
              {adversarial.map((entry, idx) => {
                const obj = (entry as Record<string, unknown>) ?? {};
                const leg = typeof obj.leg === "string" ? obj.leg : `Leg ${idx + 1}`;
                const challenge =
                  typeof obj.challenge === "string"
                    ? obj.challenge
                    : "Adversarial challenge applied.";
                const verdict =
                  typeof obj.verdict === "string" ? obj.verdict : null;
                return (
                  <FpRow key={idx} first={idx === 0} padY={8}>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          color: "var(--text-secondary)",
                        }}
                      >
                        {leg}
                      </div>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          color: "var(--text-tertiary)",
                          marginTop: 2,
                        }}
                      >
                        {challenge}
                        {verdict ? ` — verdict: ${verdict}` : ""}
                      </div>
                    </div>
                  </FpRow>
                );
              })}
            </div>
          ) : (
            <div style={{ marginTop: 8 }}>
              <FpBadge variant="amber">Adversarial Review Applied · 🔒 Pro unlocks detail</FpBadge>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
